import { useState } from "react";
import { Download, FileIcon } from "lucide-react";
import { Button } from "~/components/ui/button";
import useToast from "~/components/ui/toast";
import type { ArtifactPreview } from "~/services/artifacts";
import { defaultFormatFor } from "./formats";
import { useArtifactActions } from "./useArtifactActions";

// Shared body for "file-like" artifacts (text + non-image blob). The
// preview Lambda's FilePreview content is `{file_name}` — no inline
// data to render — so we show a labeled row with a visible Download
// affordance. The toolbar's icon-only Download is also wired, but this
// in-body button is what the user reaches for first.
export const FileArtifactBody = ({ preview }: { preview: ArtifactPreview }) => {
  const actions = useArtifactActions(preview.artifact_id);
  const { showError } = useToast();
  const filename = filenameFromPreview(preview);
  const subtype = fileSubtype(filename, preview.artifact_type);
  const defaultFormat = defaultFormatFor(preview.artifact_type);
  const [downloading, setDownloading] = useState(false);
  const onClick = async () => {
    setDownloading(true);
    try {
      const result = await actions.download(defaultFormat);
      if (!result.ok) {
        showError(`Download failed: ${result.error}`);
      }
    } finally {
      setDownloading(false);
    }
  };
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/40 p-3">
      <div className="flex size-10 items-center justify-center rounded-md border border-border bg-background">
        <FileIcon className="size-5 text-muted-foreground" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium" title={filename}>
          {filename}
        </div>
        <div className="text-xs text-muted-foreground">{subtype}</div>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={downloading}
        onClick={() => void onClick()}
        className="h-9 gap-1.5 border-border bg-background px-3 text-sm"
      >
        <Download className="size-4" aria-hidden />
        Download
      </Button>
    </div>
  );
};

function filenameFromPreview(preview: ArtifactPreview): string {
  const c = preview.content;
  if (c && typeof c === "object" && !Array.isArray(c)) {
    const fileName = (c as { file_name?: unknown }).file_name;
    if (typeof fileName === "string" && fileName) return fileName;
  }
  return preview.filename ?? preview.artifact_name ?? "Untitled file";
}

function fileSubtype(filename: string, artifactType: string): string {
  const idx = filename.lastIndexOf(".");
  const ext = idx >= 0 ? filename.slice(idx + 1) : "";
  if (ext && ext.length <= 5) return `${ext.toUpperCase()} file`;
  return artifactType === "text" ? "Text file" : "File";
}
